import { useState, type DetailedHTMLProps, type InputHTMLAttributes } from "react";


import MuiButton, { type ButtonProps as MuiButtonProps } from "@mui/material/Button";
import MuiIconButton, { type IconButtonProps } from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import useToggle from "core/hooks/useToggle";

import Icon, { type IconName } from "ui/atom/Icon";
import Link, { type RouterLinkProps } from "ui/atom/Link";
import Modal from "ui/Modal";


export type ButtonProps = MuiButtonProps;

type ClipboardBase = {
	value: string;
	i?: IconName;
	onCopied?: () => void;
};

type ClipboardAsIcon = ClipboardBase & {
	icon?: true;
} & Omit<ButtonIconProps, "icon" | "onClick">;

type ClipboardAsButton = ClipboardBase & {
	icon: false;
} & Omit<ButtonProps, "onClick" | "startIcon">;

type ButtonClipboardProps = ClipboardAsIcon | ClipboardAsButton;

type ButtonLinkProps = RouterLinkProps & ButtonProps;

export type ButtonUploadProps = {
	children?: React.ReactNode;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

type ButtonIconProps = Omit<IconButtonProps, "size"> & RouterLinkProps & {
	icon: IconName;
	fill?: string;
	size?: number;
};

type ButtonMenuProps = {
	display: string;
	selected: number;
	options: { value: number; label: string }[];
	select: (value: number) => void;
} & ButtonProps;

type ButtonShareProps = {
	url: string;
	title?: string;
	text?: string;
} & ButtonIconProps;

type CProps = { title: string; text?: string; onClick?: () => Promise<unknown> };
type WithIcon = { icon: IconName } & Omit<ButtonIconProps, "icon"> & CProps;
type WithoutIcon = { icon?: undefined } & ButtonProps & CProps;
type ConfirmationProps = WithIcon | WithoutIcon;


Button.Clipboard = (props: ButtonClipboardProps) => {
	const [copied, setCopied] = useState(false);

	const click = async () => {
		await navigator.clipboard.writeText(props.value);
		setCopied(true);
		props.onCopied?.();
		setTimeout(() => setCopied(false), 2000);
	};

	if (props.icon === false) {
		const { icon: _icon, onCopied, value, i, ...buttonProps } = props;
		return <Button {...buttonProps} startIcon={<Icon name={copied ? "check" : i || "copy"} />} onClick={click} />;
	}

	const { onCopied, value, i, ...iconProps } = props;
	return <Button.Icon {...iconProps} icon={copied ? "check" : i || "copy"} onClick={click} />;
};

Button.Icon = ({ icon, size, fill, to, children, ...props }: ButtonIconProps) => (
	<MuiIconButton component={to ? Link : undefined} href={to ? to : undefined} target={props.tab ? "_blank" : undefined} rel="noopener noreferer" {...props}>
		{icon && <Icon name={icon} size={size} fill={fill || "none"} />}
		{children}
	</MuiIconButton>
);

Button.Link = (props: ButtonLinkProps) => <Button component={Link} {...props}/>;

Button.Menu = ({ display, selected, options, select, ...props }: ButtonMenuProps) => {
	const [anchor, setAnchor] = useState<Element | null>(null);
	const open = Boolean(anchor);

	const handleClose = () => setAnchor(null);

	return (
		<>
			<Button onClick={(e) => setAnchor(e.currentTarget)} startIcon={<Icon name="chevron-down" />} {...props}>{display}</Button>
			<Menu
				open={open}
				anchorEl={anchor}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				onClick={handleClose}
				onClose={handleClose}
				keepMounted
			>
				{options.map((o, i) => <MenuItem selected={selected === o.value} onClick={() => select(o.value)} key={i}>{o.label}</MenuItem>)}
			</Menu>
		</>
	);
};

Button.Share = ({ url, title, text, icon, size, ...props }: ButtonShareProps) => {
	const shareInfo = async () => await navigator.share({ title, text, url });;

	return <Button.Icon icon={icon || "share"} size={size} onClick={shareInfo} {...props} />;
};

Button.Upload = ({ children, ...props }: ButtonUploadProps) => {
	const style = {
		input: {
			display: "none"
		}
	};

	return (
		<Button
			component="label"
			variant="contained"
			tabIndex={-1}
			startIcon={<Icon name="cloud-upload" />}
		>
			{children}
			<input accept="image/*" type="file" style={style.input} {...props}/>
		</Button>
	);
};

Button.Confirmation = (props: ConfirmationProps) => {
	const { value, toggle } = useToggle();

	// common fields for the modal
	const { title, text, onClick } = props;

	const click = async () => {
		if (onClick) await onClick();
		toggle();
	};

	const actions = [
		{ label: "Cancel", variant: "outlined", color: "default", onClick: toggle },
		{ label: "Confirm", variant: "contained", color: "error", onClick: click }
	];

	return (
		<>
			{props.icon ? (
			// props is now narrowed to WithIcon
				(() => {
					const { icon, ...rest } = props as WithIcon;
					return <Button.Icon {...rest} icon={icon} onClick={toggle} />;
				})()
			) : (
			// props is now narrowed to WithoutIcon
				(() => {
					const { icon: _ignored, ...rest } = props as WithoutIcon;
					return <Button {...rest} onClick={toggle}/>;
				})()
			)}

			<Modal.Confirmation title={title} text={text} open={value} onClose={toggle} actions={actions}/>
		</>
	);
};

export default function Button(props: ButtonProps) {
	return <MuiButton {...props} />;
}